import React, { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, YAxis, Tooltip } from "recharts";
import { Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Measured SoH history sparkline — daily snapshots recorded per VIN by
 *  the backend (/api/soh/history). Shows the real curve once >=2 days of
 *  data exist; friendly collecting state before that. */
export const SohHistoryCard = ({ vehicle }) => {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    if (!vehicle?.live || !vehicle?.vin || !API) return;
    fetch(`${API}/api/soh/history/${encodeURIComponent(vehicle.vin)}?days=180`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => setPoints(j.points || []))
      .catch(() => setPoints([]));
  }, [vehicle?.vin, vehicle?.live]);

  if (!vehicle?.live || !vehicle?.vin) return null;

  const data = (points || []).map((p) => ({
    date: p.date?.slice(5),
    soh: p.soh_pct,
  }));
  const hasChart = data.length >= 2;
  const first = hasChart ? data[0].soh : null;
  const last = hasChart ? data[data.length - 1].soh : null;
  const delta = hasChart ? +(last - first).toFixed(2) : null;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="soh-history-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.08)", borderColor: "rgba(0,229,255,0.30)" }}
        >
          <Activity className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Measured history · {points === null ? "…" : `${data.length} day${data.length === 1 ? "" : "s"}`}
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {hasChart
              ? `SoH trend: ${delta > 0 ? "+" : ""}${delta}% over ${data.length} days`
              : "Recording your daily SoH"}
          </div>
        </div>
        {vehicle.velocitySource === "measured" && (
          <span
            className="shrink-0 font-mono text-[8px] uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10]"
            data-testid="soh-measured-badge"
          >
            Measured
          </span>
        )}
      </div>

      {hasChart ? (
        <div className="mt-3 h-24" data-testid="soh-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
              <defs>
                <linearGradient id="sohFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis domain={["dataMin - 0.3", "dataMax + 0.3"]} hide />
              <Tooltip
                contentStyle={{ background: "#0a0f0a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: "#888", fontFamily: "monospace", fontSize: 10 }}
                formatter={(v) => [`${v}% SoH`, null]}
              />
              <Area type="monotone" dataKey="soh" stroke="#00E5FF" strokeWidth={2} fill="url(#sohFill)" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed mt-2" data-testid="soh-history-collecting">
          {points === null
            ? "Loading history…"
            : "One snapshot is stored automatically each day you open the app. The measured curve appears after 2+ days — and after 14 days it replaces the model with your pack's real velocity."}
        </p>
      )}
    </div>
  );
};
